import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText, Eye,
} from "lucide-react";
import { Form } from "react-bootstrap";
import {
  PageHeader, AppButton, AppBadge, EmptyState,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import apiClient from "../../utils/apiClient";
import "./institute-invoices.scss";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

const STATUS_CONFIG = {
  sent: { label: "Sent", variant: "info" },
  viewed: { label: "Viewed", variant: "primary" },
  pending_payment: { label: "Pending Payment", variant: "warning" },
  paid: { label: "Paid", variant: "success" },
  overdue: { label: "Overdue", variant: "danger" },
  cancelled: { label: "Cancelled", variant: "secondary" },
};

const SERVICE_TYPE_LABELS = {
  custom_development: "Custom Development",
  integration: "Integration",
  support: "Support",
  amc: "AMC",
  training: "Training",
  revenue_share: "Revenue Share",
  others: "Others",
};

const InstituteInvoicesPage = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;

      const { data } = await apiClient.get("/service-invoices", { params });
      setInvoices(data.invoices || []);
      setTotalPages(data.totalPages || 1);
    } catch {
      setToast({ show: true, message: "Failed to load invoices.", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  return (
    <div className="ii-page">
      <PageHeader
        icon={FileText}
        title="Service Invoices"
        description="View and pay service invoices from the platform"
      />

      {/* Filter */}
      <div className="ii-filters">
        <Form.Select
          className="ii-status-filter"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All Statuses</option>
          <option value="sent">Sent</option>
          <option value="viewed">Viewed</option>
          <option value="pending_payment">Pending Payment</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="cancelled">Cancelled</option>
        </Form.Select>
      </div>

      {loading ? (
        <div className="ii-loading">Loading invoices...</div>
      ) : invoices.length === 0 ? (
        <EmptyState
          icon={<FileText size={40} />}
          title="No invoices"
          description="You don't have any service invoices yet."
        />
      ) : (
        <div className="ii-table-wrapper">
          <table className="ii-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Service Type</th>
                <th>Amount</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const sc = STATUS_CONFIG[inv.status] || {};
                return (
                  <tr key={inv._id}>
                    <td className="ii-invoice-num">{inv.invoiceNumber}</td>
                    <td>{SERVICE_TYPE_LABELS[inv.serviceType] || inv.serviceType}</td>
                    <td className="ii-amount">{formatCurrency(inv.totalAmount)}</td>
                    <td className={inv.status === "overdue" ? "ii-overdue-date" : ""}>{formatDate(inv.dueDate)}</td>
                    <td>
                      <AppBadge variant={sc.variant}>{sc.label}</AppBadge>
                    </td>
                    <td>
                      <button
                        className="ii-action-btn"
                        onClick={() => navigate(`/service-invoices/${inv._id}`)}
                        title="View Invoice"
                      >
                        <Eye size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="ii-pagination">
          <AppButton variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </AppButton>
          <span className="ii-page-info">Page {page} of {totalPages}</span>
          <AppButton variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </AppButton>
        </div>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: "", type: "success" })}
      />
    </div>
  );
};

export default InstituteInvoicesPage;
